import { Location } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Input from '@cobliteam/ui/form/Input';
import { useDebounce } from '@cobliteam/ui/hooks';
import Icon from '@cobliteam/ui/icons';
import { cn, date } from '@cobliteam/ui/utils';
import { useEffect, useState, useTransition } from 'react';
import { Link } from 'react-router-dom';
import { vehicleLabel } from '../../../helpers';
import { useSearch } from '../../../hooks/useSearch';
import type { Vehicle, VehicleGroupWithCategory } from '../../../types';
import CardHeader from '../../Cards/CardHeader';
import type { CardItemProps } from '../../Cards/CardItem';
import CardItem from '../../Cards/CardItem';

const VehicleGroupDetails = () => {
  const { t } = useTranslation('global-search');
  const { selectedItem, setIsOpen } = useSearch();
  const [search, setSearch] = useState('');
  const [filteredVehicles, setFilteredVehicles] = useState<Vehicle[]>([]);
  const debouncedSearch = useDebounce(search, 500);
  const item = selectedItem as VehicleGroupWithCategory;
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    if (debouncedSearch) {
      startTransition(() => {
        const search = debouncedSearch.toLowerCase().trim();
        setFilteredVehicles(
          item.vehicles?.filter((vehicle) => {
            const licensePlate = vehicle?.licensePlate?.toLowerCase().trim();
            const label = vehicle?.label?.toLowerCase().trim();
            const model = vehicle?.model?.name?.toLowerCase().trim();
            const brand = vehicle?.model?.brand?.toLowerCase().trim();
            const year = vehicle?.model?.year?.toLowerCase().trim();
            return licensePlate?.includes(search) || label?.includes(search) || model?.includes(search) || brand?.includes(search) || year?.includes(search);
          }) || []
        );
      });
    } else {
      startTransition(() => {
        setFilteredVehicles(item.vehicles || []);
      });
    }
  }, [debouncedSearch, item.vehicles]);

  if (!item) return null;

  const title = item.name;
  const subtitle = `${t('vehicle_group_details.total_vehicles')} ${item?.vehicles?.length}`;

  return (
    <div className='relative flex h-[500px] w-full flex-col gap-4 overflow-hidden overflow-y-auto p-4'>
      <CardHeader
        icon={item.category.icon}
        color={item.category.color}
        link={`${Location.VEHICLES_GROUPS}?search=${item.id}`}
        linkTooltip={t('vehicle_group_details.go_to_vehicle_groups_page')}
        title={title}
        subtitle={subtitle}
      />

      {/*Details*/}
      <div className='grid grid-cols-12 gap-2'>
        <div className='col-span-12'>
          <div className='mb-1 flex items-center gap-2 font-medium text-xs'>
            <Icon name='contact-round' className='!size-3' />
            {t('vehicle_group_details.information')}
          </div>
          <div className='ml-5 w-full'>
            {/* parse data  2022-11-24T12:13:06.995Z */}
            <div className='text-xs'>
              {t('vehicle_group_details.registered')} {item?.createdAt ? date.format(item?.createdAt, 'dd/MM/yyyy - HH:mm:ss') : '---'}
            </div>
            <div className='text-xs'>
              {t('vehicle_group_details.updated')} {item?.updatedAt ? date.format(item?.updatedAt, 'dd/MM/yyyy - HH:mm:ss') : '---'}
            </div>
          </div>
        </div>
      </div>

      <div>
        {filteredVehicles?.length > 0 && (
          <div className='mb-2 flex items-center justify-between gap-2 font-medium text-xs'>
            <div className='flex flex-0.8 items-center gap-2'>
              <Icon name='contact-round' className='!size-3' />
              {t('vehicle_group_details.vehicle_list')}
            </div>

            <div className='flex flex-1 items-center gap-2'>
              <div>
                <Input
                  variant='ghost'
                  className='h-6 pl-7 text-gray-9 text-xs'
                  placeholder={t('vehicle_group_details.search_vehicle')}
                  startIcon={<Icon name={isPending ? 'loader' : 'search'} className={cn(isPending && 'animate-spin')} size='xs' color='gray-light' />}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className='w-3'>
                {search && (
                  <div onClick={() => setSearch('')} className='cursor-pointer'>
                    <Icon name='trash' size='xs' color='gray' />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className='flex min-h-[320px] flex-col gap-2.5'>
          {/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation> */}
          {filteredVehicles?.map((vehicle) => {
            const cardVehicle: CardItemProps = {
              disabled: !vehicle,
              title: vehicle ? vehicle?.licensePlate || '---' : t('vehicle_details.no_vehicle_associated'),
              subtitle: vehicle ? vehicleLabel(vehicle) : '---',
              tooltip: vehicle ? t('vehicle_details.associated_vehicle') : t('vehicle_details.no_vehicle_associated'),
              icon: 'truck',
              color: 'gray',
              itemLink: `${Location.VEHICLES}?search=${vehicle?.licensePlate}`,
              pageLink: Location.VEHICLES,
            };

            return <CardItem key={vehicle.id} {...cardVehicle} />;
          })}

          {search && filteredVehicles?.length === 0 && (
            <div className='flex h-[300px] flex-col items-center justify-center'>
              <Icon name='truck' size='md' color='gray' />
              <div className='text-gray-9 text-xs'>{t('vehicle_group_details.no_vehicles_found')}</div>
              <div className='text-gray-9 text-xs'>
                <Button startIcon={<Icon name='trash-2' className='!size-2' />} variant='ghost' size='sm' className='text-xs' onClick={() => setSearch('')}>
                  {t('vehicle_group_details.clear_filter')}
                </Button>
              </div>
            </div>
          )}

          {filteredVehicles?.length === 0 && !search && (
            <div className='flex h-[300px] flex-col items-center justify-center'>
              <Icon name='truck' size='md' color='gray' />
              <div className='text-gray-9 text-xs'>{t('vehicle_group_details.no_vehicles_added')}</div>
              <div className='text-gray-9 text-xs'>
                <Link to={`${Location.VEHICLES_GROUPS_ID.replace(':vehicleGroupId', item.id)}`} onClick={() => setIsOpen(false)} className='text-blue-5'>
                  <Button startIcon={<Icon name='plus' className='!size-2' />} variant='ghost' size='sm' className='text-xs'>
                    {t('vehicle_group_details.add_vehicle')}
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleGroupDetails;
